import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  MapPin, Heart, Sparkles, Baby, Save, 
  LogOut, X, ArrowLeft
} from "lucide-react";

// Indian cities for dropdown
const INDIAN_CITIES = [
  "Mumbai", "Delhi", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad",
  "Jaipur", "Surat", "Lucknow", "Kanpur", "Nagpur", "Indore", "Thane", "Bhopal",
  "Visakhapatnam", "Patna", "Vadodara", "Ghaziabad", "Ludhiana", "Agra", "Nashik",
  "Faridabad", "Meerut", "Rajkot", "Varanasi", "Srinagar", "Aurangabad", "Dhanbad",
  "Amritsar", "Navi Mumbai", "Allahabad", "Ranchi", "Howrah", "Coimbatore", "Jabalpur",
  "Gwalior", "Vijayawada", "Jodhpur", "Madurai", "Raipur", "Kota", "Chandigarh",
  "Guwahati", "Solapur", "Hubli", "Mysore", "Tiruchirappalli", "Bareilly", "Aligarh",
  "Tiruppur", "Moradabad", "Jalandhar", "Bhubaneswar", "Salem", "Warangal", "Guntur",
  "Bhiwandi", "Saharanpur", "Gorakhpur", "Bikaner", "Amravati", "Noida", "Jamshedpur",
  "Bhilai", "Cuttack", "Firozabad", "Kochi", "Dehradun", "Durgapur", "Asansol",
  "Nanded", "Kolhapur", "Ajmer", "Gulbarga", "Jamnagar", "Ujjain", "Loni", "Siliguri",
  "Jhansi", "Ulhasnagar", "Nellore", "Jammu", "Sangli", "Belgaum", "Mangalore",
  "Ambattur", "Tirunelveli", "Malegaon", "Gaya", "Jalgaon", "Udaipur", "Maheshtala",
  "Other"
].sort();

const PHASES = [
  { id: "ttc", label: "Trying to Conceive", icon: Sparkles, color: "clay" },
  { id: "pregnant", label: "Pregnant", icon: Heart, color: "sage" },
  { id: "postpartum", label: "Postpartum", icon: Baby, color: "purple" }
];

const Settings = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [interests, setInterests] = useState([]);
  const [formData, setFormData] = useState({
    phase: user?.phase || "",
    bio: user?.bio || "",
    location: user?.location || "",
    interests: user?.interests || []
  });

  useEffect(() => {
    fetchInterests();
  }, []);

  const fetchInterests = async () => {
    try {
      const response = await axios.get(`${API}/config/interests`);
      setInterests(response.data);
    } catch (error) {
      console.error("Fetch interests error:", error);
    }
  };

  const handlePhaseSelect = (phaseId) => {
    setFormData(prev => ({ ...prev, phase: phaseId }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/users/me`,
        formData,
        { withCredentials: true }
      );
      
      updateUser(response.data);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  // Get interest labels from API data
  const getInterestLabels = () => {
    if (interests.length > 0) {
      return interests.map(i => i.label);
    }
    // Fallback
    return [
      "Fitness & Yoga", "Nutrition", "Mental Health", "Sleep & Rest",
      "Breastfeeding", "Baby Care", "Work-Life Balance", "Relationships",
      "Self Care", "Natural Remedies", "Medical Support", "Community Events"
    ];
  };

  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      
      {/* Header */}
      <header className="px-6 pt-6 pb-4">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-sage-dark mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-2xl font-serif text-sage-darker mb-2">Settings</h1>
          <p className="text-muted-foreground text-sm">Update your profile and preferences</p>
        </div>
      </header>

      <main className="px-6 max-w-2xl mx-auto space-y-6" data-testid="settings-main">
        {/* Phase Selection */}
        <Card className="bg-white border-sage/20" data-testid="phase-settings">
          <CardHeader>
            <CardTitle className="text-lg font-serif text-sage-darker">Your Phase</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {PHASES.map((phase) => {
              const Icon = phase.icon;
              const isSelected = formData.phase === phase.id;
              return (
                <button
                  key={phase.id}
                  onClick={() => handlePhaseSelect(phase.id)}
                  className={`w-full p-4 rounded-xl border transition-all duration-300 text-left flex items-center gap-4 ${
                    isSelected
                      ? 'border-sage bg-sage/10'
                      : 'border-sage/20 bg-white hover:border-sage/40'
                  }`}
                  data-testid={`phase-${phase.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    phase.color === 'sage' ? 'bg-sage/20' :
                    phase.color === 'clay' ? 'bg-clay/20' : 'bg-purple-100'
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      phase.color === 'sage' ? 'text-sage-dark' :
                      phase.color === 'clay' ? 'text-clay-dark' : 'text-purple-600'
                    }`} />
                  </div>
                  <span className="text-sage-darker flex-1">{phase.label}</span>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    isSelected ? 'border-sage bg-sage' : 'border-sage/30'
                  }`}>
                    {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Bio & Location */}
        <Card className="bg-white border-sage/20" data-testid="bio-settings">
          <CardHeader>
            <CardTitle className="text-lg font-serif text-sage-darker">About You</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sage-darker">Bio</Label>
              <Textarea
                placeholder="Share a little about yourself..."
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                className="min-h-[100px] bg-sage/5 border-sage/20 text-sage-darker placeholder:text-muted-foreground focus:border-sage resize-none"
                data-testid="bio-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sage-darker">City</Label>
              <Select 
                value={formData.location} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
              >
                <SelectTrigger className="bg-sage/5 border-sage/20 text-sage-darker" data-testid="location-select">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <SelectValue placeholder="Select your city" />
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {INDIAN_CITIES.map((city) => (
                    <SelectItem key={city} value={city}>
                      {city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Interests */}
        <Card className="bg-white border-sage/20" data-testid="interests-settings">
          <CardHeader>
            <CardTitle className="text-lg font-serif text-sage-darker">Interests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {getInterestLabels().map((interest) => {
                const isSelected = formData.interests.includes(interest);
                return (
                  <button
                    key={interest}
                    onClick={() => handleInterestToggle(interest)}
                    className={`px-3 py-1.5 rounded-full border text-sm transition-all duration-300 ${
                      isSelected
                        ? 'border-sage bg-sage/20 text-sage-dark'
                        : 'border-sage/20 text-muted-foreground hover:border-sage/40'
                    }`}
                    data-testid={`interest-${interest.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {interest}
                    {isSelected && <X className="w-3 h-3 ml-1 inline" />}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={loading}
          className="w-full bg-sage text-white hover:bg-sage-dark rounded-full py-6 font-medium"
          data-testid="save-settings-btn"
        >
          {loading ? (
            "Saving..."
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Save Changes
            </>
          )}
        </Button>

        <Separator className="bg-sage/20" />

        {/* Logout */}
        <Card className="bg-white border-sage/20" data-testid="logout-section">
          <CardContent className="p-5">
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full border-red-200 text-red-500 hover:bg-red-50 rounded-full"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Settings;
